import { Directive, HostListener, Input, output } from '@angular/core';
import { isObject } from 'lodash-es';

import { VariationProduct } from 'ish-core/models/product/product.model';

@Directive({ selector: '[customAppSortable]' })
export class SortableDirective {
  @Input('customAppSortable') sortableColumn = '';
  @Input() products: VariationProduct[] = [];
  @Input() productPrice: number[] = [];
  @Input() variableVariationAttributes: string[] = [];
  readonly sortedProducts = output<VariationProduct[]>();
  readonly headerClickedChange = output<string>();
  readonly sortOrderChange = output<'asc' | 'desc'>();

  sortOrder: 'asc' | 'desc' = 'asc';
  headerClicked = '';

  @HostListener('click') onClick() {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';

    this.headerClicked = this.sortableColumn;

    this.headerClickedChange.emit(this.headerClicked);
    this.sortOrderChange.emit(this.sortOrder);

    if (this.sortableColumn === 'price') {
      this.sortProductsByPrice();
    } else {
      const columnIndex = this.variableVariationAttributes.findIndex(attr => attr === this.sortableColumn);
      if (columnIndex !== -1) {
        this.sortProductsByAttribute(columnIndex);
      } else {
        this.sortProductsByColumn();
      }
    }

    this.sortedProducts.emit(this.products);
  }

  // eslint-disable-next-line complexity
  private handleNA(valueA: string | number, valueB: string | number): number {
    const isA_NA = valueA === '' || valueA === -1;
    const isB_NA = valueB === '' || valueB === -1;

    if (isA_NA && !isB_NA) {
      return 1;
    } // Place 'A_N/A' at the end
    if (!isA_NA && isB_NA) {
      return -1;
    } // Place 'B_N/A' at the end
    if (isA_NA && isB_NA) {
      return 0;
    } // If both are 'N/A', keep their relative order

    // If neither value is 'N/A', return 2 to indicate no handling was necessary
    return 2;
  }

  private sortProductsByAttribute(index: number) {
    this.products.sort((a, b) => {
      const valueA = a.variableVariationAttributes[index]?.value as string | number; // (as string | number) ADDED DURING MIGRATION TO BE REMOVED WHILE VALIDATING
      const valueB = b.variableVariationAttributes[index]?.value as string | number; // (as string | number) ADDED DURING MIGRATION TO BE REMOVED WHILE VALIDATING

      const notAvailableResult = this.handleNA(
        !isObject(valueA) && valueA ? valueA : '',
        !isObject(valueB) && valueB ? valueB : ''
      );
      if (notAvailableResult !== 2) {
        return notAvailableResult;
      } // Handle 'N/A' comparison directly

      if (this.sortableColumn === 'Hard drive size' || this.sortableColumn === 'Hard disk drive capacity') {
        const bytesA = this.convertToBytes(valueA);
        const bytesB = this.convertToBytes(valueB);
        return this.sortOrder === 'desc' ? bytesB - bytesA : bytesA - bytesB;
      } else {
        return this.sortOrder === 'desc'
          ? valueB?.toString().localeCompare(valueA.toString())
          : valueA?.toString().localeCompare(valueB.toString()); //toString() ADDED DURING MIGRATION TO BE REMOVED WHILE VALIDATING
      }
    });
  }

  private sortProductsByPrice() {
    this.products.forEach((_, i) => {
      const priceElement = document.getElementById(`priceElementId${i}`);

      if (priceElement) {
        let priceValue = '';
        if (priceElement.innerText.includes('€')) {
          priceValue = priceElement.innerText.replace(/[^\d.,]/g, '').replace(',', '.');
        } else if (priceElement.innerText.includes('$')) {
          const parts = priceElement.innerText.trim().split('$');
          if (parts[1]) {
            priceValue = parts[1].replace(',', '');
          }
        } else if (priceElement.innerText.includes('N/A')) {
          priceValue = '-1.00';
        }

        this.productPrice[i] = parseFloat(priceValue);
      }
    });

    const pairedArray = this.productPrice.map((value, index) => ({
      value,
      corresponding: this.products[index],
    }));

    pairedArray.sort((first, second) => {
      const notAvailableResult = this.handleNA(first.value, second.value);
      if (notAvailableResult !== 2) {
        return notAvailableResult;
      } // Handle 'N/A' comparison directly

      return this.sortOrder === 'desc' ? second.value - first.value : first.value - second.value;
    });

    this.products = pairedArray.map(product => product.corresponding);
  }

  private sortProductsByColumn() {
    const key = this.sortableColumn as keyof (typeof this.products)[0];

    this.products.sort((a, b) => {
      const valueA = a[key] as string;
      const valueB = b[key] as string;

      const notAvailableResult = this.handleNA(valueA, valueB);
      if (notAvailableResult !== 2) {
        return notAvailableResult;
      } // Handle 'N/A' comparison directly

      return this.sortOrder === 'desc' ? valueB?.localeCompare(valueA) : valueA?.localeCompare(valueB);
    });
  }

  private convertToBytes(memory: string | number): number {
    // CHANGED string to (string | number) ADDED DURING MIGRATION TO BE REMOVED WHILE VALIDATING
    const regex = /(\d+)\s*(\w+)/;
    const match = memory.toString().match(regex); //toString() ADDED DURING MIGRATION TO BE REMOVED WHILE VALIDATING
    if (!match) {
      return 0;
    }

    const number = parseInt(match[1], 10);

    const unit = match[2].toLowerCase();

    switch (unit) {
      case 'kb':
        return number * 1024;
      case 'mb':
        return number * 1024 ** 2;
      case 'gb':
        return number * 1024 ** 3;
      case 'tb':
        return number * 1024 ** 4;
      default:
        return number;
    }
  }
}
